import { Typography } from 'antd'
import { IconFont } from '../iconfont'
import { CheckOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { t } from '@/i18n'
import {
  arduinoLight,
  githubGist,
  tomorrow,
  stackoverflowLight,
  magula,
} from 'react-syntax-highlighter/dist/esm/styles/hljs'

interface HighlighterProps {
  text: string
  matchList: string[]
  language?: string
  theme?: any
}

interface TextPart {
  text: string
  matches: string[]
}

const Highlighter = ({ text = '', matchList = [], language, theme = vs2015 }: HighlighterProps) => {
  const getHighlightedText = () => {
    const charMatches = Array.from(text).map((char, index) => ({
      char,
      matches: [] as string[],
    }))

    matchList.forEach(keyword => {
      let start = text.indexOf(keyword)
      while (start !== -1) {
        const end = start + keyword.length
        for (let i = start; i < end; i++) {
          charMatches[i].matches.push(keyword)
        }
        start = text.indexOf(keyword, start + 1)
      }
    })

    const parts: TextPart[] = []
    let currentMatches = charMatches[0]?.matches || []
    let currentText = charMatches[0]?.char || ''

    for (let i = 1; i < charMatches.length; i++) {
      if (JSON.stringify(currentMatches) === JSON.stringify(charMatches[i].matches)) {
        currentText += charMatches[i].char
      } else {
        parts.push({ text: currentText, matches: currentMatches })
        currentText = charMatches[i].char
        currentMatches = charMatches[i].matches
      }
    }
    parts.push({ text: currentText, matches: currentMatches })

    return parts
  }

  const parts = getHighlightedText()

  const highlightedText = parts.map((part, index) => {
    if (part.matches.length > 0) {
      return (
        <SyntaxHighlighter
          language={language}
          style={theme}
          wrapLines
          customStyle={{
            display: 'inline',
            padding: 0,
            background: 'transparent',
          }}
          lineProps={{
            style: {
              wordBreak: 'break-all',
              overflow: 'hidden',
              fontFamily: 'SauceCodePro Nerd Font Mono',
              backgroundColor: '#4169e198',
              fontSize: '14px',
            },
          }}
          wrapLongLines={{ whiteSpace: 'pre-wrap' }}
        >
          {part.text}
        </SyntaxHighlighter>
      )
    } else {
      return (
        <SyntaxHighlighter
          key={`plain-${index}`}
          language={language}
          style={theme}
          wrapLines
          customStyle={{
            display: 'inline',
            padding: 0,
            background: 'transparent',
          }}
          lineProps={{
            style: {
              wordBreak: 'break-all',
              overflow: 'hidden',
              fontFamily: 'SauceCodePro Nerd Font Mono',
              fontSize: '14px',
            },
          }}
          wrapLongLines={{ whiteSpace: 'pre-wrap' }}
        >
          {part.text}
        </SyntaxHighlighter>
      )
    }
  })

  return <div>{highlightedText}</div>
}

interface CodeSnippetProps {
  code: any
  matchList?: string[]
  language?: string
  cutCode?: boolean
  /* 
    1. 端口服务列表Banner
    2. WEB应用列表响应内容
    3. WEB应用详情响应内容
    4. 代码仓库、公开文档、网盘资源、暗网情报、IM监控详情源文件、来源依据（关键字高亮，字体调整）
    5. 漏洞风险、高危服务、SSL证书风险请求响应流量
  */
  type: number
  copyText?: string
}

export const CodeSnippet = ({
  code,
  type,
  language,
  matchList = [],
  cutCode = false,
  copyText = '', //传递要copy的内容
}: CodeSnippetProps) => {
  let text
  let codeText
  let theme = type == 1 || type == 2 ? githubGist : vs2015
  switch (type) {
    case 1:
      text = JSON.stringify(code)
      codeText = code
      break
    case 2:
      text = `${JSON.stringify(code[0])}${code[1]}`
      codeText = [
        Object?.keys(code[0])
          ?.map(item => `${item}: ${code[0][item]}`)
          ?.join('\n'),
        code[1],
      ]

      break
    case 3:
      text = copyText ? copyText : `${JSON.stringify(code[0])}${code[1]}`
      codeText = code[0]
        ? [
            Object?.keys(code[0])
              ?.map(item => `${item}: ${code[0]?.[item]}`)
              ?.join('\n'),
            code[1],
          ]
        : [code[1]]
      break
    case 4:
      text = code
      codeText = code
      break
    case 5:
      text =
        code == '空' ||
        code == 'none' ||
        code == null ||
        code == '' ||
        code == 'null' ||
        code?.match(/^\s+$/)
          ? '无'
          : code
      codeText = text
      break
    case 6: // http请求request url + header + body
      text = copyText ? copyText : `${code[2]}${JSON.stringify(code[0])}${code[1]}`
      codeText = code[0]
        ? [
            Object?.keys(code[0])
              ?.map(item => `${item}: ${code[0]?.[item]}`)
              ?.join('\n'),
            code[1],
            code[2],
          ]
        : [code[1]]
      break
    default:
      text = code
      codeText = text
  }

  if (cutCode) {
    let keywordMatches: number[] = []
    let matchedLines: { line: string; index: number }[] = []
    const codeTextList = codeText?.split(/\n/)

    matchList.forEach(keyword => {
      codeTextList.forEach((line, index) => {
        let start = line.indexOf(keyword)
        while (start !== -1) {
          keywordMatches.push(index)
          matchedLines.push({ line, index })
          start = line.indexOf(keyword, start + 1)
        }
      })
    })

    let selectedLines: string[] = []
    if (keywordMatches.length > 0) {
      const firstMatchIndex = keywordMatches[0]
      const lastMatchIndex = keywordMatches[keywordMatches.length - 1]

      const startLine = Math.max(0, firstMatchIndex - 5)
      const endLine = Math.min(codeText.length - 1, lastMatchIndex + 5)
      selectedLines = codeTextList.slice(startLine, endLine + 1)
      codeText = selectedLines?.join('\n')
    }
  }

  return (
    <ResponseContent
      type={type}
      style={{
        margin: '16px 0 0 0',
        width: '100%',
      }}
    >
      <div className="response-up">
        <div className="title-left">
          <div className="pie pie1" />
          <div className="pie pie2" />
          <div className="pie pie3" />
        </div>
        <Typography.Paragraph
          copyable={{
            icon: [
              <div className="copy">
                <IconFont
                  type="icon-fuzhi"
                  style={{ color: type == 1 || type == 2 ? '#666' : '#fff' }}
                />

                <span
                  style={{ marginLeft: '6px', color: type == 1 || type == 2 ? '#666' : '#fff' }}
                >
                  {t('复制')}
                </span>
              </div>,
              <div>
                <CheckOutlined style={{ color: type == 1 || type == 2 ? '#2090A8' : '#fff' }} />
                <span
                  style={{ color: type == 1 || type == 2 ? '#2090A8' : '#fff', marginLeft: '6px' }}
                >
                  {t('复制')}
                </span>
              </div>,
            ],

            tooltips: false,
            text: text,
          }}
          style={{ margin: '0px 0px 0px 8px' }}
        />
      </div>
      <div className="response-content">
        {codeText instanceof Array ? (
          <>
            {type == 6 && (
              <>
                <Highlighter
                  text={codeText[2]}
                  matchList={matchList}
                  language={'http'}
                  theme={theme}
                />
              </>
            )}
            <Highlighter text={codeText[0]} matchList={matchList} language={'http'} theme={theme} />
            <Highlighter text={codeText[1]} matchList={matchList} language={'html'} theme={theme} />
          </>
        ) : (
          <Highlighter text={codeText} matchList={matchList} language={language} theme={theme} />
        )}
      </div>
    </ResponseContent>
  )
}

const ResponseContent = styled.div`
  margin: 16px 0 24px 0;
  overflow: hidden;
  .copy {
    color: #d0d0d0;
    &:hover {
      color: #fff;
    }
  }
  .response-up {
    padding: 0 16px;
    display: flex;
    justify-content: space-between;
    height: 36px;
    line-height: 36px;
    background: ${props => (props.type == 1 || props.type == 2 ? '#e4e4e4' : '#37353d')};
    border-radius: 8px 8px 0 0;
    .title-left {
      display: flex;
      align-items: center;
      gap: 8px;
      .pie {
        width: 14px;
        height: 14px;
        border-radius: 50%;
      }
      .pie1 {
        background: #ff5f56;
      }
      .pie2 {
        background: #ffbb2f;
      }
      .pie3 {
        background: #27c841;
      }
    }
  }
  .response-content {
    min-height: ${props => (props.type == 1 || props.type == 2 ? '160px' : 'auto')};
    background: ${props => (props.type == 1 || props.type == 2 ? '#f1f1f1' : '#1e1e1e')};
    border-radius: 0 0 8px 8px;
    padding: 16px 19px;
    color: ${props => (props.type == 1 || props.type == 2 ? '#333333' : '#fff')};
    display: flex;
    flex-direction: column;
    font-family: 'SauceCodePro Nerd Font Mono';
    gap: 6px;
    pre {
      ${props =>
        props.type == 1 || props.type == 2 ? 'background-color: #f1f1f1 !important;' : ''}
      margin: 0;
    }
  }
`
